import type { Command } from "../document/commands.ts";
import type { MapDocument } from "../document/types.ts";
import { MAX_BRUSH_SIZE, MIN_BRUSH_SIZE } from "./coords.ts";
import { updateTerrainSession, type TerrainSession } from "./incremental.ts";
import { beginStroke, extendStroke, finishStroke, type Stroke } from "./stroke.ts";
import type { Repaint } from "./store.ts";

/**
 * Terrain brush strokes (ADR-020, ADR-021): the stroke in progress, its incremental session, the brush
 * size and the active paint colour. The painter reads the map and sessions through its host and hands
 * finished strokes back as commands; the store keeps history, sessions and repaint channels.
 */

/**
 * What the painter needs from the store.
 */
export interface PaintHost {
  readonly mapId: () => string;
  readonly map: () => MapDocument;
  readonly activeLayerId: () => string | null;
  readonly refusesEdits: (layerId: string) => boolean;
  readonly session: (layerId: string) => TerrainSession | undefined;
  readonly record: (command: Command) => boolean;
  readonly emitRepaint: (repaint: Repaint) => void;
  readonly chosenColour: (layerId: string) => number | undefined;
}

/**
 * The painter.
 */
export class TerrainPainter {
  private stroke: Stroke | null = null;
  private strokeSession: TerrainSession | null = null;
  private size = 1;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: PaintHost) {}

  /**
   * Whether a stroke is in progress.
   *
   * @returns True mid-stroke.
   */
  public get painting(): boolean {
    return this.stroke !== null;
  }

  /**
   * Brush size in vertices.
   *
   * @returns The size.
   */
  public get brushSize(): number {
    return this.size;
  }

  /**
   * Sets the brush size.
   *
   * @param size Brush size.
   * @throws Error when the size is outside the allowed range.
   */
  public setBrushSize(size: number): void {
    if (!Number.isInteger(size) || size < MIN_BRUSH_SIZE || size > MAX_BRUSH_SIZE) {
      throw new Error(`brush size ${size} is outside ${MIN_BRUSH_SIZE} to ${MAX_BRUSH_SIZE}`);
    }
    this.size = size;
  }

  /**
   * The paint colour for the active layer (ADR-038): the highest colour its corners hold; for an unpainted
   * layer the colour chosen for it in the palette, or 1.
   *
   * @returns The colour.
   */
  public activeColour(): number {
    const layerId = this.host.activeLayerId();
    return layerId === null ? 1 : this.layerColour(layerId);
  }

  /**
   * The paint colour of a terrain layer: the highest colour its corners hold; for an unpainted layer the
   * colour chosen for it in the palette, or 1. A layer of another kind reads 1.
   *
   * @param layerId Layer id.
   * @returns The colour.
   */
  public layerColour(layerId: string): number {
    const layer = this.host.map().layers.find((l) => l.id === layerId);
    if (layer === undefined || layer.kind !== "terrain") {
      return 1;
    }
    let colour = 0;
    for (let i = 0; i < layer.corners.length; i += 1) {
      colour = Math.max(colour, layer.corners[i] as number);
    }
    return colour === 0 ? (this.host.chosenColour(layer.id) ?? 1) : colour;
  }

  /**
   * Starts a stroke on the active layer.
   *
   * @param erase True to paint colour 0.
   * @returns False when there is no active editable (unlocked, shown) terrain layer or a stroke is in progress.
   */
  public begin(erase: boolean): boolean {
    const layerId = this.host.activeLayerId();
    const session = layerId === null ? undefined : this.host.session(layerId);
    if (layerId === null || session === undefined || this.stroke !== null || this.host.refusesEdits(layerId)) {
      return false;
    }
    const map = this.host.map();
    const layer = map.layers.find((l) => l.id === layerId);
    if (layer === undefined || layer.kind !== "terrain") {
      return false;
    }
    this.stroke = beginStroke(this.host.mapId(), layerId, layer.corners, map.width, map.height, erase ? 0 : this.activeColour(), session.corners);
    this.strokeSession = session;
    return true;
  }

  /**
   * Paints the brush at a vertex during a stroke and re-resolves incrementally.
   *
   * @param vx Vertex column.
   * @param vy Vertex row.
   * @returns Cells to repaint, or null when no stroke is in progress.
   */
  public extend(vx: number, vy: number): Repaint | null {
    if (this.stroke === null || this.strokeSession === null) {
      return null;
    }
    const bounds = extendStroke(this.stroke, vx, vy, this.size);
    const repaint = { layerId: this.stroke.layerId, rect: updateTerrainSession(this.strokeSession, bounds) };
    this.host.emitRepaint(repaint);
    return repaint;
  }

  /**
   * Ends the stroke, recording it when it changed anything. A stroke the host refuses (its layer locked or hidden)
   * is discarded as `cancel` discards it.
   *
   * @returns True when a command was recorded.
   */
  public finish(): boolean {
    const stroke = this.stroke;
    const session = this.strokeSession;
    if (stroke === null || session === null) {
      return false;
    }
    this.stroke = null;
    this.strokeSession = null;
    const command = finishStroke(stroke);
    if (command === null) {
      return false;
    }
    if (!this.host.record(command)) {
      this.discard(stroke, session);
      return false;
    }
    return true;
  }

  /**
   * Discards the stroke in progress when its layer is locked (ADR-043 decision 3) or hidden (P1.6-T9): a
   * lock or an eye that lands mid-stroke takes back what the stroke painted, so nothing reaches the layer.
   */
  public releaseRefused(): void {
    const stroke = this.stroke;
    const session = this.strokeSession;
    if (stroke === null || session === null || !this.host.refusesEdits(stroke.layerId)) {
      return;
    }
    this.stroke = null;
    this.strokeSession = null;
    this.discard(stroke, session);
  }

  /**
   * Restores the vertices a stroke painted to their colour before it, re-resolves them and repaints. The
   * stroke's preview is the session's own corners, so this returns the session to the layer's corners.
   *
   * @param stroke The ended stroke.
   * @param session The session it painted into.
   */
  private discard(stroke: Stroke, session: TerrainSession): void {
    if (stroke.changes.size === 0) {
      return;
    }
    const stride = stroke.width + 1;
    let bounds = { vx0: stroke.width, vy0: stroke.height, vx1: 0, vy1: 0 };
    for (const index of stroke.changes.keys()) {
      stroke.preview[index] = stroke.before[index] as number;
      const vx = index % stride;
      const vy = Math.floor(index / stride);
      bounds = { vx0: Math.min(bounds.vx0, vx), vy0: Math.min(bounds.vy0, vy), vx1: Math.max(bounds.vx1, vx), vy1: Math.max(bounds.vy1, vy) };
    }
    this.host.emitRepaint({ layerId: stroke.layerId, rect: updateTerrainSession(session, bounds) });
  }
}
